import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { httpClient, type ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query/query-keys';

import type { InboxMessage, InboxMeta } from './types';

export interface Inbox {
  items: InboxMessage[];
  meta: InboxMeta;
}

/**
 * La boîte de réception du consultant connecté.
 *
 * `GET /me/messages` : l'identité vient du jeton. Aucune route n'accepte
 * d'identifiant de compte, ce qui rend impossible de lire la boîte d'un autre.
 *
 * ── Pourquoi un rafraîchissement automatique ─────────────────────────────
 * Un consultant laisse son portail ouvert pendant qu'il remplit son CRA. Un
 * message déposé entre-temps doit apparaître sans qu'il pense à recharger —
 * c'est la seule différence utile entre une boîte de réception et une page
 * statique. Cinq minutes suffisent : ces messages ne sont pas urgents, et
 * interroger plus souvent coûterait une requête par minute et par onglet
 * ouvert pour rien.
 */
export const useInbox = (page = 1): UseQueryResult<Inbox, ApiError> =>
  useQuery<Inbox, ApiError>({
    queryKey: queryKeys.messages.list(page),
    queryFn: () =>
      httpClient.getPaginated<InboxMessage, InboxMeta>('/me/messages', {
        query: { page, limit: 20 },
      }),
    staleTime: 60 * 1_000,
    refetchInterval: 5 * 60 * 1_000,
    /* Garde la page précédente affichée pendant le chargement de la suivante :
       sans cela, la liste disparaît une fraction de seconde à chaque
       pagination. */
    placeholderData: (previous) => previous,
  });

/**
 * Marque un message comme lu.
 *
 * Idempotent côté serveur : c'est la première lecture qui est datée, pas la
 * dernière. Le client peut donc l'appeler sans se demander s'il l'a déjà fait.
 */
export const useMarkRead = (): UseMutationResult<InboxMessage, ApiError, string> => {
  const queryClient = useQueryClient();

  return useMutation<InboxMessage, ApiError, string>({
    mutationFn: (id) => httpClient.post<InboxMessage>(`/me/messages/${id}/lu`),
    onSuccess: () => {
      /* Toute la liste, et pas seulement la ligne : le compteur de non lus
         voyage dans l'enveloppe, et c'est lui qui alimente la pastille de
         navigation. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.messages.all });
    },
  });
};

/**
 * Nombre de messages non lus, pour la pastille de navigation.
 *
 * Lu depuis le cache de la première page plutôt que par une requête dédiée :
 * la boîte est déjà chargée dès qu'on y passe, et l'entête n'a pas besoin de
 * la redemander.
 */
export const useUnreadCount = (): number => {
  const inbox = useInbox();
  return inbox.data?.meta.unread ?? 0;
};
